/* eslint-disable no-empty,no-console,no-shadow,consistent-return,no-param-reassign */
import apiGateway from '@/services/jApiGateway';
import apiProfile from '@/services/jApiProfile';
import apiUser from '@/services/jApiUser';
import jwtDecode from 'jwt-decode';
import waitText from '@/utils/waitText';

const state = {
  // Set default state(s) here
  idToken: null,
  refreshToken: null,
  refresh_token: null,
  profile_data: null,
  bg_initial_profile: '',
  is_refresh_token: false,
  login_failed: false,
  data_expire: null,
  data_expire_refresh_token: null,
  roles: [],
  packages: [],
  features: [],
  data_otp: null,
  data_login_otp: null,
  // Pending two-factor sign-in challenge. Memory only, never localStorage.
  login_challenge: null,
};

const getters = {
  // Change value of state(s) with getter
};

const mutations = {
  loginFailed(state) {
    state.login_failed = !state.login_failed;
  },

  setBgInitialProfile() {
    const colors = [
      '#dc2626',
      '#d97706',
      '#65a30d',
      '#059669',
      '#0284c7',
      '#4f46e5',
      '#9333ea',
      '#db2777',
      '#e11d48',
    ];
    state.bg_initial_profile = colors[Math.floor(Math.random() * colors.length)];
  },

  saveToken(state, data) {
    function parseJwt(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    }
    state.data_expire = parseJwt(data.token);
    state.idToken = data.token;
    state.refreshToken = data.refresh_token;
    state.is_refresh_token = !state.is_refresh_token;
    localStorage.setItem('gateUrl', process.env.GATE_URL);
  },

  saveTokenV2(state, data) {
    function parseJwt(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    }
    state.data_expire = parseJwt(data.token);
    state.idToken = data.token;
  },

  setRefreshToken(state, data) {
    function parseJwt(token) {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    }
    state.refresh_token = data.refresh_token;
    state.data_expire_refresh_token = parseJwt(data.refresh_token);

    const tokenData = {
      value: state.refresh_token,
      expire: state.data_expire_refresh_token.exp * 1000,
    };
    localStorage.setItem('refresh_token', JSON.stringify(tokenData));
  },

  setInLocalStorage(state) {
    const tokenData = {
      value: state.idToken,
      expire: state.data_expire.exp * 1000,
    };
    localStorage.setItem('Token', JSON.stringify(tokenData));
    localStorage.setItem('refreshToken', state.refreshToken);
  },

  doLogin() {
    window.location = '/';
  },

  setProfile(state, payload) {
    state.profile_data = payload;
    localStorage.setItem('email', state.profile_data.email);
    localStorage.setItem('photo', state.profile_data.photo_url);
    localStorage.setItem('firstName', state.profile_data.first_name);
    localStorage.setItem('lastName', state.profile_data.last_name);
    localStorage.setItem('userCompanyId', state.profile_data.user_company_id);
    localStorage.setItem('companyName', '');
    localStorage.setItem('daysRemaining', '15');
  },

  setRole() {
    const session = jwtDecode(JSON.parse(localStorage.getItem('Token')).value);
    localStorage.setItem('userRole', session && session.user && session.user.user_role ? session.user.user_role : null);
  },

  setUserShowOtp(state, data) {
    state.data_otp = data;
  },

  setDataLoginOtp(state, data) {
    state.data_login_otp = data;
  },

  setLoginChallenge(state, data) {
    state.login_challenge = {
      challenge_id: data.challenge_id,
      masked_email: data.masked_email,
      code_expires_in_seconds: data.code_expires_in_seconds,
      resend_available_in_seconds: data.resend_available_in_seconds,
    };
  },

  setLoginChallengeTimers(state, data) {
    state.login_challenge = {
      ...state.login_challenge,
      code_expires_in_seconds: data.code_expires_in_seconds,
      resend_available_in_seconds: data.resend_available_in_seconds,
    };
  },

  clearLoginChallenge(state) {
    state.login_challenge = null;
  },
};

function withCooldown(err) {
  const headers = err.response && err.response.headers;
  const seconds = headers ? Number(headers['x-login-cooldown-seconds']) : NaN;
  if (err.response && err.response.status === 429 && !Number.isNaN(seconds)) {
    err.cooldownSeconds = seconds;
  }
  return err;
}

function lockedMessage(seconds) {
  return `Too many incorrect codes. For your security, please wait ${waitText(seconds)} before trying again.`;
}

// Stores the token pair, loads the profile and enters the app.
async function completeSignIn(commit, data) {
  commit('saveToken', data);
  commit('setRefreshToken', data);
  commit('setInLocalStorage');
  const getProfil = await apiProfile.getProfile();
  commit('setProfile', getProfil);
  commit('setRole');
  commit('doLogin');
}

const actions = {
  async loginAccount({ commit, dispatch }, authData) {
    dispatch('jStoreNotificationScreen/toggleLoading', 'Please wait...', { root: true });
    try {
      const auth = await apiGateway.login(authData);
      commit('saveToken', auth);
      commit('setInLocalStorage');
      const getProfil = await apiProfile.getProfile();
      commit('setProfile', getProfil);
      commit('setRole');
      commit('doLogin');
    } catch (err) {
      dispatch('jStoreNotificationScreen/screenProblem', err.response.data.message, { root: true });
    }
    dispatch('jStoreNotificationScreen/toggleLoading', '', { root: true });
  },

  async loginAccountV2({ commit, dispatch }, authData) {
    dispatch('jStoreNotificationScreen/toggleLoading', 'Please wait...', { root: true });
    try {
      const auth = await apiGateway.loginV2(authData);
      if (auth.data.two_factor_required) {
        commit('setLoginChallenge', auth.data);
      } else if (auth.data.token) {
        await completeSignIn(commit, auth.data);
      } else {
        dispatch('jStoreNotificationScreen/toggleProblem', 'Token is empty', { root: true });
      }
    } catch (err) {
      const { code, message } = err.response.data;
      const { cooldownSeconds } = withCooldown(err);
      const locked = code === 'two_factor_locked' && cooldownSeconds !== undefined;
      // A lock is a wait, not a failure, so it is a warning; other errors stay as before.
      dispatch(`jStoreNotificationScreen/${locked ? 'toggleWarning' : 'toggleProblem'}`, locked ? lockedMessage(cooldownSeconds) : message, { root: true });
    }
    dispatch('jStoreNotificationScreen/toggleLoading', '', { root: true });
  },

  // Verify and resend reject with the axios error so the code step can map it.
  // On a 429 the error carries cooldownSeconds from X-Login-Cooldown-Seconds.
  async verifyLoginChallenge({ state, commit }, otp) {
    if (!state.login_challenge) throw new Error('No pending login challenge');
    try {
      const auth = await apiGateway.verifyLoginChallenge(state.login_challenge.challenge_id, otp);
      if (!auth.data || !auth.data.token) throw new Error('Token is empty');
      // No clearLoginChallenge: doLogin reloads the page, and clearing first would swap the
      // code step for the empty legacy step while the browser is still leaving.
      await completeSignIn(commit, auth.data);
    } catch (err) {
      throw withCooldown(err);
    }
  },

  async resendLoginChallenge({ state, commit }) {
    if (!state.login_challenge) throw new Error('No pending login challenge');
    try {
      const res = await apiGateway.resendLoginChallenge(state.login_challenge.challenge_id);
      if (res && res.data) commit('setLoginChallengeTimers', res.data);
    } catch (err) {
      throw withCooldown(err);
    }
  },

  async LoginLdapAccount({ commit, dispatch }, authData) {
    dispatch('jStoreNotificationScreen/toggleLoading', 'Please wait...', { root: true });
    try {
      const auth = await apiGateway.loginLdap(authData);
      commit('saveToken', auth);
      commit('setInLocalStorage');
      const getProfil = await apiProfile.getProfile();
      const getRole = await apiUser.userAuthorize();
      commit('setProfile', getProfil);
      localStorage.setItem('role', JSON.stringify(getRole.data.role.states));
      localStorage.setItem('userRole', 1);
      commit('doLogin');
    } catch (err) {
      dispatch('jStoreNotificationScreen/toggleProblem', err.response.data.message, { root: true });
    }
    dispatch('jStoreNotificationScreen/toggleLoading', '', { root: true });
  },

  async getProfileAndRole({ commit }) {
    try {
      const getProfil = await apiProfile.getProfile();
      commit('setProfile', getProfil);
      commit('setRole');
    } catch (err) {
    }
  },

  async getKeyPublicRSA({ dispatch }) {
    let res;
    try {
      res = await apiGateway.getKeyRSA();
    } catch (err) {
      dispatch('jStoreNotificationScreen/toggleProblem', err.response.data.message, { root: true });
    }
    return res;
  },

  async refreshTokenApiV2({ commit }) {
    try {
      const newToken = await apiGateway.refreshTokenV2();
      commit('saveTokenV2', newToken.data);
      commit('setInLocalStorage');
    } catch (err) {
    }
  },

  async requestOtpLogin({ commit, dispatch }, req) {
    dispatch('jStoreNotificationScreen/showLoading', 'Please Wait', { root: true });
    try {
      const res = await apiGateway.requestOtpLogin(req);
      dispatch('jStoreNotificationScreen/hideLoading', '', { root: true });
      commit('setDataLoginOtp', req);
      commit('setUserShowOtp', res.data);
    } catch (err) {
      dispatch('jStoreNotificationScreen/hideLoading', '', { root: true });
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};



// WEBPACK FOOTER //
// ./src/stores/jStoreLogin.js